import { useState } from 'react';
import { Users, Gift, Sparkles, Check, Copy, Coins } from 'lucide-react';
import { invitationAPI } from '../../services/api';
import type { InvitationConfig } from './types';

interface InviteBannerProps {
  config: InvitationConfig | null;
  isLoggedIn: boolean;
  onLogin: () => void;
}

export function InviteBanner({ config, isLoggedIn, onLogin }: InviteBannerProps) {
  const [copied, setCopied] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  if (!config?.inviteEnabled) return null;

  const rebateEnabled = !!config.rebateEnabled;
  const rebateCreditsPerYuan = config.rebateCreditsPerYuan ?? 0;
  const rebatePercent = config.rebatePercent ?? 0;
  const rebatePerYuanLabel = Number.isInteger(rebateCreditsPerYuan)
    ? String(rebateCreditsPerYuan)
    : rebateCreditsPerYuan.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  const rebatePercentLabel = Number.isInteger(rebatePercent)
    ? String(rebatePercent)
    : rebatePercent.toFixed(1);

  const handleCopy = async () => {
    if (!isLoggedIn) {
      onLogin();
      return;
    }
    try {
      const res = await invitationAPI.getMyInvitations();
      await navigator.clipboard.writeText(res.inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 忽略错误
    }
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6">
      {/* Main Banner Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-500 rounded-xl flex items-center justify-center shrink-0">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-green-800">
              邀请好友赚积分
            </h3>
            <p className="text-xs text-green-700 mt-0.5 leading-relaxed">
              邀请好友注册，您得 <span className="font-semibold">{config.inviterCredits}</span> 积分，好友得 <span className="font-semibold">{config.inviteeCredits}</span> 积分
            </p>
            {rebateEnabled && (
              <p className="text-xs text-green-700 mt-0.5 leading-relaxed">
                好友充值，您返 <span className="font-semibold">{rebatePerYuanLabel}</span> 积分/元（按实付金额返 <span className="font-semibold">{rebatePercentLabel}%</span>
                {config.rebateMinOrderAmount
                  ? `，单笔满 ¥${config.rebateMinOrderAmount} 起返`
                  : ''}）
              </p>
            )}
            {config.invitePhoneOnly && (
              <p className="text-xs text-green-600 mt-1 leading-relaxed">
                仅限手机号注册可享受邀请奖励
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 sm:shrink-0">
          <button
            onClick={() => setShowDetail(!showDetail)}
            className="text-xs text-green-700 hover:text-green-900 underline px-1"
          >
            {showDetail ? '收起' : '查看详情'}
          </button>
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5 shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{copied ? '已复制' : (isLoggedIn ? '复制邀请链接' : '登录获取链接')}</span>
            <span className="sm:hidden">{copied ? '已复制' : (isLoggedIn ? '复制链接' : '登录')}</span>
          </button>
        </div>
      </div>

      {/* Detail Panel */}
      {showDetail && (
        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-green-200">
          <div
            className={`grid grid-cols-1 ${
              rebateEnabled ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'
            } gap-3 sm:gap-4 text-sm`}
          >
            <div className="bg-white rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-green-600" />
                <span className="font-medium text-green-800">邀请人奖励</span>
              </div>
              <p className="text-green-700 text-xs sm:text-sm leading-relaxed">
                每成功邀请 1 位好友注册，您可获得 <span className="font-bold">{config.inviterCredits}</span> 积分
              </p>
            </div>
            <div className="bg-white rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-green-600" />
                <span className="font-medium text-green-800">被邀请人奖励</span>
              </div>
              <p className="text-green-700 text-xs sm:text-sm leading-relaxed">
                好友通过您的链接注册，可额外获得 <span className="font-bold">{config.inviteeCredits}</span> 积分
              </p>
            </div>
            {rebateEnabled && (
              <div className="bg-white rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Coins className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-800">充值返利</span>
                </div>
                <p className="text-green-700 text-xs sm:text-sm leading-relaxed">
                  好友充值，您可获得 <span className="font-bold">{rebatePerYuanLabel}</span> 积分/元
                  （按实付金额返 <span className="font-bold">{rebatePercentLabel}%</span>）
                  {config.rebateMinOrderAmount
                    ? `，单笔满 ¥${config.rebateMinOrderAmount} 起返`
                    : ''}
                </p>
              </div>
            )}
          </div>
          {!isLoggedIn && (
            <p className="text-xs text-green-600 mt-3 text-center">
              登录后即可获取您的专属邀请链接
            </p>
          )}
          {config.invitePhoneOnly && (
            <p className="text-xs text-green-600 mt-2 text-center">
              仅限好友使用手机号注册时，双方方可获得邀请奖励
            </p>
          )}
        </div>
      )}
    </div>
  );
}
